import { ATTR_NAME } from 'constants/attr-name'
import { CLASS_NAME } from 'constants/class-names'
import { EventType, focusAbleElementsSelector } from 'constants/enums'
import { CompareEventType, MAX_VEHICLES } from '../car-comparison/constants'
import { IDrivingStore, IDrivingVehicle } from '../driving-interface'
import { CarComparisonStore } from '../store'
import { AbstractCard } from '../abstract-card'
import { TrimSelectorModel } from '../trim-selector/trim-selector-model'

const { findCompare: className } = CLASS_NAME
const {
    driving: { modelCard: attrName }
} = ATTR_NAME

/**
 * Model Card Component 🚘
 */
export class ModelCard extends AbstractCard {
    protected get openTrimSelectorButtonClass(): string {
        return `.${className.buttonAddToCompare}`
    }

    protected get makeNameClass(): string {
        return `.${className.makeName}`
    }

    protected get modelNameClass(): string {
        return `.${className.modelName}`
    }

    protected get yearAttr(): string {
        return attrName.year
    }

    protected get makeAttr(): string {
        return attrName.make
    }

    protected get modelAttr(): string {
        return attrName.model
    }

    protected get vehicleCardIDAttr(): string {
        return attrName.vehicleCardID
    }

    public get trimSelector(): TrimSelectorModel {
        return CarComparisonStore.getInstance().getTrimSelectorModel()
    }

    public constructor(public wrapper: HTMLElement) {
        super(wrapper)
        CarComparisonStore.getInstance().registerModelCard(this)
    }

    /**
     * Bind HTML Element for find compare page
     * @returns {boolean} Indicate if the HTML structure is on the screen
     */
    protected bindHTMLElements(): boolean {
        if (!super.bindHTMLElements()) {
            return false
        }

        const removeButton = this.getElement<HTMLButtonElement>(
            `.${className.buttonRemove}`,
            this.wrapper
        )

        if (!removeButton) {
            return false
        }

        this.removeButton = removeButton
        return true
    }

    /**
     * Bind event listeners
     */
    protected bindListeners(): void {
        super.bindListeners()

        // Remove vehicle in my picks
        this.removeButton.addEventListener(EventType.CLICK, () => {
            CarComparisonStore.getInstance()
                .get()
                .forEach((vehicle) => {
                    if (this.isVehicleMatched(vehicle)) {
                        CarComparisonStore.getInstance().remove(vehicle.id)
                    }
                })
            this.focusCard()
        })

        // Update card for removed vehicle
        document.addEventListener(
            CompareEventType.DRIVING_COMPARE_REMOVE,
            () => {
                this.setCardState()
            }
        )
    }

    /**
     * Set the state comparing with store (already added/ compare)
     */
    public setCardState(): void {
        // Set compare now button link based on session for all vehicle cards
        const vehicles = CarComparisonStore.getInstance().get()
        this.wrapper.classList.remove(className.cardAdded)

        this.openTrimSelectorButton.hidden = true
        this.removeButton.hidden = true

        let showAddToCompare = true
        vehicles.forEach((vehicle) => {
            if (this.isVehicleMatched(vehicle)) {
                showAddToCompare = false
                this.wrapper.classList.add(className.cardAdded)
            }
        })

        this.enableCompare()
        // Adjust hidden status of buttons
        if (showAddToCompare) {
            this.openTrimSelectorButton.hidden = false
        } else {
            this.removeButton.hidden = false
        }
    }

    protected openTrimSelector(): void {
        if (CarComparisonStore.getInstance().get().length === MAX_VEHICLES) {
            CarComparisonStore.getInstance().getLimitExceededModal().openModal()
            return
        }
        super.openTrimSelector()
    }

    private isVehicleMatched(vehicle: IDrivingStore<IDrivingVehicle>): boolean {
        if (!vehicle.item) {
            return false
        }

        if (this.vehicleCardID) {
            return this.vehicleCardID === String(vehicle.item.id)
        }

        return (
            this.year === vehicle.item.year &&
            this.make === vehicle.item.model.make.slugified_name &&
            this.model === vehicle.item.model.slugified_name
        )
    }

    public disableCompare(): void {
        this.openTrimSelectorButton.disabled = true
    }

    public enableCompare(): void {
        this.openTrimSelectorButton.disabled = false
    }

    public focusCard(): void {
        const focusableEls = this.getElements(
            focusAbleElementsSelector,
            this.wrapper
        )
        if (focusableEls.length) {
            focusableEls[0].focus()
        }
    }
}
